/**
 * Lead Links — per-prospect upload share links, for Priori staff.
 *
 * The primary admin page for lead upload links — /admin/lead-links redirects
 * here. Reads data through GET /api/v1/admin/lead-links. A link is a
 * capability: whoever holds the URL can upload a game video (plus optional
 * tracking/metadata/events files) with no account, so creating one is a
 * platform-ADMIN act while listing, downloading and revoking are
 * platform-read (matching the server's gates in app/routes/lead_upload.py).
 *
 * Uploads land in R2 under lead-uploads/<slug>/<slot>/<filename>, in the
 * environment's own bucket. Downloads go through the API endpoint
 * (GET /api/v1/admin/lead-links/<id>/download/<slot>) which returns a
 * presigned URL; the page opens it in a new tab. Platform admins can also
 * turn a link's uploads into a club's matches (LeadImport.tsx).
 */
import { Copy, Link2, ShieldOff } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { ApiError, api, type Session } from '../lib/api'
import LeadImport from './LeadImport'
import './admin.css'

type SlotState = {
  filename: string | null
  size_bytes: number | null
  status: string | null
  completed_at: string | null
}
type LeadLink = {
  id: number
  slug: string
  url: string
  lead_name: string
  lead_email: string | null
  lead_org_label: string | null
  created_at: string
  expires_at: string | null
  revoked_at: string | null
  slots: Record<string, SlotState>
}
type Payload = {
  links: LeadLink[]
  slots: Record<string, string>
  max_upload_gb: number
}
type UploadedFile = {
  key: string
  slot: string
  filename: string
  size_bytes: number | null
  last_modified: string | null
}

function fmtSize(bytes: number | null): string {
  if (!bytes) return ''
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined,
    { year: 'numeric', month: 'short', day: 'numeric' })
}

export default function LeadLinks({ session }: { session: Session }) {
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [copiedId, setCopiedId] = useState<number | null>(null)

  const load = useCallback(() => {
    api<Payload>('/api/v1/admin/lead-links')
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [])
  useEffect(load, [load])

  const shown = useMemo(() => {
    if (!data) return []
    const q = query.trim().toLowerCase()
    if (!q) return data.links
    return data.links.filter((l) =>
      l.lead_name.toLowerCase().includes(q)
      || (l.lead_email ?? '').toLowerCase().includes(q)
      || (l.lead_org_label ?? '').toLowerCase().includes(q))
  }, [data, query])

  const copyUrl = (link: LeadLink) => {
    void navigator.clipboard.writeText(link.url)
    setCopiedId(link.id)
    window.setTimeout(() => setCopiedId((c) => (c === link.id ? null : c)), 1500)
  }

  if (error) return <p className="error">{error}</p>
  if (!data) return <p className="muted">Loading…</p>

  return (
    <>
      <h1 className="page-title">Lead Links</h1>
      <p className="page-sub">
        Per-prospect upload links. Whoever holds a link can upload a match
        video (up to {data.max_upload_gb} GB) and optional data files with no
        account — treat the URL itself as the credential. Uploads notify the
        lead-upload Slack channel when its webhook is configured.
      </p>

      <div className="adm-bar">
        <label className="adm-field">
          <span className="t-label">Find</span>
          <input className="adm-input" style={{ minWidth: 280 }}
                 placeholder="Search by name, email, or club"
                 value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <span className="adm-bar__spacer" />
        <span className="t-data" style={{ color: 'var(--color-text-faint)' }}>
          {shown.length}
        </span>
      </div>

      <CreateLinkPanel canCreate={session.user.is_platform_admin}
                       onCreated={load} />

      <table className="adm-table">
        <thead>
          <tr><th>Lead</th><th>Created</th><th>Uploads</th><th>Link</th><th></th></tr>
        </thead>
        <tbody>
          {shown.map((l) => (
            <tr key={l.id} className={l.revoked_at ? 'is-revoked' : undefined}>
              <td>
                <div>{l.lead_name}</div>
                <div className="t-data" style={{ color: 'var(--color-text-muted)' }}>
                  {[l.lead_org_label, l.lead_email].filter(Boolean).join(' · ')}
                </div>
              </td>
              <td>{fmtDate(l.created_at)}</td>
              <td>
                {Object.entries(l.slots)
                  .filter(([, s]) => s.status === 'completed')
                  .map(([slot, s]) => (
                    <div key={slot}>
                      <button type="button" className="pg-link"
                        onClick={async () => {
                          try {
                            const r = await api<{ url: string }>(
                              `/api/v1/admin/lead-links/${l.id}/download/${slot}`)
                            window.open(r.url, '_blank')
                          } catch (e) {
                          alert(e instanceof Error ? e.message : 'Download failed — try again.')
                        }
                        }}>
                        {data.slots[slot] ?? slot}
                      </button>{' '}
                      <span className="t-data"
                            style={{ color: 'var(--color-text-faint)' }}>
                        {fmtSize(s.size_bytes)}
                      </span>
                    </div>
                  ))}
                {Object.values(l.slots).every((s) => s.status !== 'completed') && (
                  <span className="muted">nothing yet</span>
                )}
                {/* The slot buttons above show only the LATEST upload per
                    slot — a second upload through the same link overwrites
                    the row's pointer while the earlier object stays in R2.
                    This lists the link's whole bucket prefix, so every file
                    a prospect sent is reachable. */}
                <FilesToggle linkId={l.id} slotLabels={data.slots} />
                {/* Turning uploads into a club's games writes to the
                    database and the club's R2 folder: platform admins. */}
                {session.user.is_platform_admin && <LeadImport linkId={l.id} />}
              </td>
              <td>
                {l.revoked_at ? (
                  <span className="muted">revoked {fmtDate(l.revoked_at)}</span>
                ) : (
                  <button className="adm-btn adm-btn--quiet"
                          onClick={() => copyUrl(l)}>
                    <Copy size={12} />
                    {copiedId === l.id ? 'Copied' : 'Copy link'}
                  </button>
                )}
              </td>
              <td>
                {!l.revoked_at && (
                  <RevokeButton link={l} onRevoked={load} />
                )}
              </td>
            </tr>
          ))}
          {shown.length === 0 && (
            <tr><td colSpan={5} className="adm-empty">No link matches the current filter.</td></tr>
          )}
        </tbody>
      </table>
    </>
  )
}

/* Creating a link is an ops act (platform.admin) — the server refuses
   non-admins with a 404 either way; hiding the panel just keeps the page
   honest about what the person can do. */
function CreateLinkPanel({ canCreate, onCreated }: {
  canCreate: boolean; onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [org, setOrg] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  if (!canCreate) return null

  const submit = async () => {
    setBusy(true); setMsg(null)
    try {
      const d = await api<{ url: string }>(
        '/api/v1/admin/lead-links/create',
        {
          method: 'POST',
          body: JSON.stringify({
            lead_name: name.trim(),
            lead_email: email.trim(),
            lead_org_label: org.trim(),
          }),
        })
      setMsg({ ok: true, text: `Link created: ${d.url}` })
      setName(''); setEmail(''); setOrg('')
      onCreated()
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally { setBusy(false) }
  }

  return (
    <div className="panel adm-add">
      <div className="adm-bar">
        <label className="adm-field">
          <span className="t-label">Lead name</span>
          <input className="adm-input" style={{ minWidth: 220 }}
                 placeholder="Jane Coach" value={name}
                 onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="adm-field">
          <span className="t-label">Email (optional)</span>
          <input className="adm-input" placeholder="jane@club.com" value={email}
                 onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="adm-field">
          <span className="t-label">Club (optional)</span>
          <input className="adm-input" placeholder="Example FC" value={org}
                 onChange={(e) => setOrg(e.target.value)} />
        </label>
      </div>
      <div className="adm-actions">
        <button className="adm-btn" disabled={busy || name.trim() === ''}
                onClick={() => void submit()}>
          <Link2 size={13} />
          Create Lead Link
        </button>
      </div>
      {msg && <p className={`adm-msg adm-msg--${msg.ok ? 'ok' : 'err'}`}>{msg.text}</p>}
    </div>
  )
}

/** On-demand list of EVERYTHING under the link's R2 prefix. Fetched only
 *  when opened — the table renders up to 200 rows and a bucket listing per
 *  row on load would be 200 R2 round-trips for data nobody asked for. */
function FilesToggle({ linkId, slotLabels }: {
  linkId: number; slotLabels: Record<string, string>
}) {
  const [open, setOpen] = useState(false)
  const [files, setFiles] = useState<UploadedFile[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next && files === null) {
      api<{ files: UploadedFile[] }>(`/api/v1/admin/lead-links/${linkId}/files`)
        .then((r) => setFiles(r.files))
        .catch((e: unknown) => setError(
          e instanceof ApiError ? e.message : 'Could not list files.'))
    }
  }

  const download = async (f: UploadedFile) => {
    try {
      const r = await api<{ url: string }>(
        `/api/v1/admin/lead-links/${linkId}/files/download`
        + `?key=${encodeURIComponent(f.key)}`)
      window.open(r.url, '_blank')
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Download failed — try again.')
    }
  }

  return (
    <div>
      <button type="button" className="pg-link t-data"
              style={{ color: 'var(--color-text-faint)' }}
              onClick={toggle}>
        {open ? 'hide files' : 'all files…'}
      </button>
      {open && error && <div className="error t-data">{error}</div>}
      {open && !error && files === null && (
        <div className="muted t-data">Loading…</div>
      )}
      {open && files !== null && files.length === 0 && (
        <div className="muted t-data">No files in storage for this link.</div>
      )}
      {open && files !== null && files.map((f) => (
        <div key={f.key} className="t-data">
          <button type="button" className="pg-link"
                  onClick={() => void download(f)}>
            {f.filename}
          </button>{' '}
          <span style={{ color: 'var(--color-text-faint)' }}>
            {[slotLabels[f.slot] ?? f.slot, fmtSize(f.size_bytes),
              fmtDate(f.last_modified)].filter(Boolean).join(' · ')}
          </span>
        </div>
      ))}
    </div>
  )
}

function RevokeButton({ link, onRevoked }: { link: LeadLink; onRevoked: () => void }) {
  const [busy, setBusy] = useState(false)
  const revoke = async () => {
    if (!window.confirm(`Revoke the upload link for ${link.lead_name}? `
      + 'The URL stops working; completed uploads are kept.')) return
    setBusy(true)
    try {
      await api(`/api/v1/admin/lead-links/${link.id}/revoke`, { method: 'POST' })
      onRevoked()
    } finally { setBusy(false) }
  }
  return (
    <button className="adm-btn adm-btn--danger" disabled={busy}
            onClick={() => void revoke()}>
      <ShieldOff size={12} />
      Revoke
    </button>
  )
}
